import { useState, useEffect, useRef } from 'react';
import { Card, Table, Tag, Empty, Spin, Button, Modal, Form, Input, message, Space, Drawer } from 'antd';
import { PlusOutlined, EyeOutlined, SendOutlined, MessageOutlined } from '@ant-design/icons';
import { feedbackAPI } from '../services/api';
import { t } from '../utils/i18n';

const { TextArea } = Input;

const Feedback = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState(null);
  const [chatVisible, setChatVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [form] = Form.useForm();
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const fetchFeedbacks = async () => {
    try {
      const res = await feedbackAPI.getList();
      if (res.code === 0) {
        setFeedbacks(res.data || []);
      }
    } catch (error) {
      console.error('获取反馈列表失败', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (feedbackId) => {
    setMessagesLoading(true);
    try {
      const res = await feedbackAPI.getMessages(feedbackId);
      if (res.code === 0) {
        setMessages(res.data || []);
      }
    } catch (error) {
      console.error('获取消息列表失败', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const getTypeTag = (type) => {
    const map = {
      contact: { color: 'gold', text: t('business_cooperation', 'Business Cooperation') },
      feedback: { color: 'purple', text: t('feedback', 'Feedback') },
    };
    const item = map[type] || map.feedback;
    return <Tag color={item.color} style={{ borderRadius: 10 }}>{item.text}</Tag>;
  };

  const getStatusTag = (status) => {
    const map = {
      pending: { color: 'orange', text: t('ticket_pending') },
      replied: { color: 'green', text: t('ticket_resolved') },
      closed: { color: 'default', text: t('ticket_closed') },
    };
    const item = map[status] || map.pending;
    return <Tag color={item.color} style={{ borderRadius: 10 }}>{item.text}</Tag>;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await feedbackAPI.submit({
        type: 'feedback',
        name: values.name || '',
        email: values.email,
        content: values.content,
      });
      message.success(t('feedback_success_msg'));
      form.resetFields();
      setModalVisible(false);
      fetchFeedbacks();
    } catch (error) {
      message.error(t('submit_failed', 'Submit failed, please try again'));
    } finally {
      setSubmitting(false);
    }
  };

  // 打开聊天窗口
  const handleOpenChat = (record) => {
    setCurrentFeedback(record);
    setChatVisible(true);
    setMessages([]);
    setInputValue('');
    fetchMessages(record.id);
  };

  // 发送消息
  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;
    if (currentFeedback?.status === 'closed') {
      message.warning(t('ticket_closed_warning', 'Ticket is closed, cannot continue conversation'));
      return;
    }

    setSubmitting(true);
    try {
      await feedbackAPI.addReply(currentFeedback.id, inputValue.trim());
      setInputValue('');
      // 刷新消息列表
      const res = await feedbackAPI.getMessages(currentFeedback.id);
      if (res.code === 0) {
        setMessages(res.data || []);
      }
      // 刷新列表状态
      fetchFeedbacks();
    } catch (error) {
      message.error(t('send_failed', 'Send failed, please try again'));
    } finally {
      setSubmitting(false);
    }
  };

  const canReply = (status) => {
    return status !== 'closed';
  };

  const getActionText = (status) => {
    if (status === 'pending') return t('view_conversation');
    if (status === 'replied') return t('continue_conversation');
    return t('view_conversation');
  };

  const columns = [
    {
      title: t('ticket_type'),
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (type) => getTypeTag(type),
    },
    {
      title: t('ticket_content'),
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
    },
    {
      title: t('ticket_status'),
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status) => getStatusTag(status),
    },
    {
      title: t('submit_time'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (time) => time ? (
        <span style={{ color: '#666', fontSize: 12 }}>{time.replace('T', ' ').substring(0, 19)}</span>
      ) : '-',
    },
    {
      title: t('actions'),
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleOpenChat(record)}>
            {getActionText(record.status)}
          </Button>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="user-page-wrapper">
      <div className="user-page-container">
        {/* 页面标题 */}
        <div className="page-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className="title-icon">
              <MessageOutlined />
            </div>
            {t('feedback')}
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
            {t('submit_ticket')}
          </Button>
        </div>

        <Card>
          {feedbacks.length === 0 ? (
            <Empty description={t('no_feedback_records')} />
          ) : (
            <Table
              columns={columns}
              dataSource={feedbacks}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          )}
        </Card>

      {/* 提交工单弹窗 */}
      <Modal
        title={t('submit_ticket')}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          form.resetFields();
          setModalVisible(false);
        }}
        confirmLoading={submitting}
        okText={t('submit')}
        cancelText={t('cancel')}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label={t('name_optional')}>
            <Input placeholder={t('enter_name')} />
          </Form.Item>

          <Form.Item
            name="email"
            label={t('email')}
            rules={[
              { required: true, message: t('enter_email') },
              { type: 'email', message: t('enter_valid_email') },
            ]}
          >
            <Input placeholder={t('enter_email')} />
          </Form.Item>

          <Form.Item
            name="content"
            label={t('issue_description')}
            rules={[{ required: true, message: t('enter_issue_description') }]}
          >
            <TextArea rows={5} placeholder={t('enter_issue_description')} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 聊天式对话窗口 */}
      <Drawer
        title={
          currentFeedback && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600 }}>{t('ticket')} #{currentFeedback.id}</span>
              {getTypeTag(currentFeedback.type)}
              {getStatusTag(currentFeedback.status)}
            </div>
          )
        }
        placement="right"
        open={chatVisible}
        onClose={() => setChatVisible(false)}
        width={480}
        styles={{
          body: { display: 'flex', flexDirection: 'column', padding: 0 },
        }}
      >
        {/* 消息列表区域 */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: 16, 
          background: '#f5f5f5',
        }}>
          {messagesLoading ? (
            <div style={{ textAlign: 'center', padding: 50 }}>
              <Spin />
            </div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: 50 }}>
              {t('no_messages')}
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  display: 'flex',
                  flexDirection: msg.sender_type === 'user' ? 'row-reverse' : 'row',
                  marginBottom: 16,
                }}
              >
                {/* 头像 */}
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: msg.sender_type === 'user'
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'linear-gradient(135deg, #1890ff 0%, #0050b3 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {msg.sender_type === 'user' ? t('me') : t('customer_service').charAt(0)}
                </div>
                {/* 消息内容 */}
                <div style={{ 
                  maxWidth: '70%', 
                  margin: msg.sender_type === 'user' ? '0 12px 0 0' : '0 0 0 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: msg.sender_type === 'user' ? 'flex-end' : 'flex-start',
                }}>
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>
                    {msg.sender_type === 'user' ? t('me') : t('customer_service')}
                    {' · '}
                    {msg.created_at?.replace('T', ' ').substring(5, 16)}
                  </div>
                  <div
                    style={{
                      padding: '10px 14px',
                      borderRadius: 12,
                      background: msg.sender_type === 'user' ? '#1890ff' : '#fff',
                      color: msg.sender_type === 'user' ? '#fff' : '#333',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6,
                      wordBreak: 'break-word',
                    }}
                  >
                    {msg.content}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* 输入区域 */}
        {currentFeedback?.status === 'closed' ? (
          <div style={{ 
            padding: 16, 
            textAlign: 'center', 
            color: '#999', 
            background: '#fafafa',
            borderTop: '1px solid #f0f0f0',
          }}>
            {t('ticket_closed_cannot_reply')}
          </div>
        ) : (
          <div style={{ 
            padding: 12, 
            background: '#fff', 
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            gap: 8,
          }}>
            <TextArea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t('enter_message')}
              autoSize={{ minRows: 1, maxRows: 4 }}
              style={{ flex: 1, borderRadius: 20, resize: 'none' }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSendMessage}
              loading={submitting}
              disabled={!inputValue.trim()}
              style={{ borderRadius: 20, height: 'auto' }}
            />
          </div>
        )}
      </Drawer>
      </div>
    </div>
  );
};

export default Feedback;
